import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Store as StoreIcon,
  ContactMail as ContactIcon,
  LocationOn as LocationIcon,
  Schedule as BusinessHoursIcon,
  RateReview as ReviewIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import StoreService from '../../services/storeService';
import { clearStoreRegistrationData } from '../../utils/storeRegistrationStorage';
import { getFunctionalItem, setFunctionalItem } from '../../utils/functionalStorage';
import LegalTermsCheckbox from '../../components/legal/LegalTermsCheckbox';
import { useAppData } from '../../context/AppDataContext';
import { useAuth } from '../../context/AuthContext';
import useStorePlan from '../../hooks/useStorePlan';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import StoreBasicInfo from '../../components/store-registration/StoreBasicInfo';
import StoreContactInfo from '../../components/store-registration/StoreContactInfo';
import StoreLocationInfo from '../../components/store-registration/StoreLocationInfo';
import StoreBusinessHoursInfo from '../../components/store-registration/StoreBusinessHoursInfo';
import StoreReview from '../../components/store-registration/StoreReview';
import { DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import { getDefaultTimeZone } from '../../utils/timeZoneUtils';
import { composePhoneValue, DEFAULT_PHONE_COUNTRY_CODE, normalizeFormPhoneFields } from '../../utils/phoneUtils';
import { DEFAULT_LANGUAGE, DEFAULT_STORE_SUPPORTED_LANGUAGES, SUPPORTED_LANGUAGE_CODES, normalizeLanguage } from '../../constants/Languages';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import AuthHeaderActions from '../../components/auth/AuthHeaderActions';
import StoreCreationBlockedAlert from '../../components/stores/StoreCreationBlockedAlert';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { LANDING_PRICING, LANDING_PRICING_CURRENCY } from '../../constants/LandingPricing';
import PageSeo from '../../components/seo/PageSeo';
import { getSlugFormatErrorKey } from '../../utils/storeSlugUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const ADMIN_STORE_REGISTRATION_STORAGE_KEY = 'salovare_storeRegistration_temp';
const SELF_SERVICE_STORE_REGISTRATION_STORAGE_KEY = 'salovare_storeOwnerRegistration_temp';

const createInitialFormData = () => ({
  name: '',
  slug: '',
  slugManuallyEdited: false,
  currency: DEFAULT_CURRENCY,
  supportedLanguages: [...DEFAULT_STORE_SUPPORTED_LANGUAGES],
  storeLanguage: DEFAULT_LANGUAGE,
  about: Object.fromEntries(SUPPORTED_LANGUAGE_CODES.map(code => [code, ''])),
  ownerName: '',
  ownerEmail: '',
  ownerPhoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
  ownerPhoneNumber: '',
  email: '',
  phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
  phoneNumber: '',
  website: '',
  vatNumber: '',
  googleReviewUrl: '',
  address: '',
  door: '',
  additionalInfo: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  timeZone: getDefaultTimeZone(),
  slotInterval: 'Thirty',
  businessHours: {
    monday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
    tuesday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
    wednesday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
    thursday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
    friday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
    saturday: { closed: false, periods: [{ from: '09:00', to: '16:00' }] },
    sunday: { closed: true, periods: [{ from: '09:00', to: '18:00' }] }
  }
});

const StoreRegistrationPage = ({ mode = 'admin' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshStores, currentStore } = useAppData();
  const { user, loading: authLoading } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const isSelfService = mode === 'self-service';
  const { entitlements } = useStorePlan(currentStore?.id, !isSelfService && Boolean(currentStore?.id));
  const userType = user?.userType || user?.user_type;
  const isAppAdmin = userType === 1 || userType === '1' || userType === 'AppAdmin' || userType === 'appAdmin';
  const canCreateStore = isAppAdmin || hasPermission(Permissions.StoresCreate);
  const storageKey = isSelfService
    ? SELF_SERVICE_STORE_REGISTRATION_STORAGE_KEY
    : ADMIN_STORE_REGISTRATION_STORAGE_KEY;
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(() => createInitialFormData());
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [acceptedLegalTerms, setAcceptedLegalTerms] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [creationEligibility, setCreationEligibility] = useState(null);
  const [creationEligibilityLoading, setCreationEligibilityLoading] = useState(false);
  const [confirmPaidDialogOpen, setConfirmPaidDialogOpen] = useState(false);
  const isCompletingRegistration = useRef(false);
  const hasLoadedFromStorage = useRef(false);

  // Local storage functions
  const saveToLocalStorage = useCallback((data) => {
    try {
      setFunctionalItem(storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save store registration data to localStorage:', error);
    }
  }, [storageKey]);

  const loadFromLocalStorage = useCallback(() => {
    try {
      const saved = getFunctionalItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.warn('Failed to load store registration data from localStorage:', error);
      return null;
    }
  }, [storageKey]);

  const clearLocalStorage = useCallback(() => {
    clearStoreRegistrationData(storageKey);
  }, [storageKey]);

  // Load data from localStorage on component mount
  useEffect(() => {
    const savedData = loadFromLocalStorage();
    if (savedData && savedData.formData) {
      const { services: _ignoredServices, ...savedFormData } = savedData.formData;
      setFormData((prev) => normalizeFormPhoneFields({
        ...prev,
        ...savedFormData,
        timeZone: savedFormData.timeZone || prev.timeZone,
        storeLanguage: normalizeLanguage(savedFormData.storeLanguage || savedFormData.supportedLanguages?.[0] || prev.storeLanguage),
      }));
      const maxStepIndex = isSelfService ? 3 : 4;
      setActiveStep(Math.min(savedData.activeStep || 0, maxStepIndex));
    }
    hasLoadedFromStorage.current = true;
  }, [isSelfService, loadFromLocalStorage]);

  // Save data to localStorage whenever formData or activeStep changes
  // Only save after we've loaded from storage to avoid overwriting
  useEffect(() => {
    if (hasLoadedFromStorage.current) {
      const dataToSave = {
        formData,
        activeStep,
        timestamp: Date.now()
      };
      saveToLocalStorage(dataToSave);
    }
  }, [formData, activeStep, saveToLocalStorage]);

  // Cleanup localStorage when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      // Only clear if we're not completing the registration
      if (!isCompletingRegistration.current) {
        clearLocalStorage();
      }
    };
  }, [clearLocalStorage]);

  useEffect(() => {
    if (isSelfService || isAppAdmin || !canCreateStore) {
      return undefined;
    }

    let cancelled = false;

    const loadCreationEligibility = async () => {
      setCreationEligibilityLoading(true);
      try {
        const eligibility = await StoreService.getStoreCreationEligibility();
        if (!cancelled) {
          setCreationEligibility(eligibility);
        }
      } catch (error) {
        console.error('Failed to load store creation eligibility:', error);
      } finally {
        if (!cancelled) {
          setCreationEligibilityLoading(false);
        }
      }
    };

    loadCreationEligibility();

    return () => {
      cancelled = true;
    };
  }, [isSelfService, isAppAdmin, canCreateStore]);

  const estimatedMonthlyPriceLabel = useMemo(() => {
    const amount = creationEligibility?.estimatedMonthlyAmount ?? LANDING_PRICING.introductoryMonthly;
    const currency = creationEligibility?.currency ?? LANDING_PRICING_CURRENCY;
    return formatCurrency(amount, currency);
  }, [creationEligibility]);

  const estimatedProratedPriceLabel = useMemo(() => {
    const amount = creationEligibility?.estimatedProratedAmount;
    if (amount == null) {
      return null;
    }

    const currency = creationEligibility?.currency ?? LANDING_PRICING_CURRENCY;
    return formatCurrency(amount, currency);
  }, [creationEligibility]);

  const nextBillingDateLabel = useMemo(() => {
    const nextBillingAtUtc = creationEligibility?.nextBillingAtUtc;
    if (!nextBillingAtUtc) {
      return null;
    }

    return formatUtcDate(nextBillingAtUtc);
  }, [creationEligibility]);

  const paidStoreWarningMessage = useMemo(() => {
    if (estimatedProratedPriceLabel && nextBillingDateLabel) {
      return t('storeRegistration.paidStoreWarningProrated', {
        price: estimatedMonthlyPriceLabel,
        proratedPrice: estimatedProratedPriceLabel,
        nextBillingDate: nextBillingDateLabel
      });
    }

    return t('storeRegistration.paidStoreWarning', { price: estimatedMonthlyPriceLabel });
  }, [estimatedMonthlyPriceLabel, estimatedProratedPriceLabel, nextBillingDateLabel, t]);

  const paidStoreConfirmMessage = useMemo(() => {
    if (estimatedProratedPriceLabel && nextBillingDateLabel) {
      return t('storeRegistration.paidStoreConfirmMessageProrated', {
        price: estimatedMonthlyPriceLabel,
        proratedPrice: estimatedProratedPriceLabel,
        nextBillingDate: nextBillingDateLabel
      });
    }

    return t('storeRegistration.paidStoreConfirmMessage', { price: estimatedMonthlyPriceLabel });
  }, [estimatedMonthlyPriceLabel, estimatedProratedPriceLabel, nextBillingDateLabel, t]);

  const extraStoreBlocked = !isSelfService
    && !isAppAdmin
    && Boolean(entitlements)
    && entitlements.allowsExtraStores === false;
  const isCreationBlocked = extraStoreBlocked || (
    !isSelfService
    && !isAppAdmin
    && creationEligibility
    && !creationEligibility.canCreate
  );

  const billingSetupPath = extraStoreBlocked && currentStore?.id
    ? `/management/stores/${currentStore.id}/payment-settings`
    : (creationEligibility?.billingSetupStoreId
      ? `/management/stores/${creationEligibility.billingSetupStoreId}/payment-settings`
      : null);

  const steps = isSelfService
    ? [
        {
          label: t('storeRegistrationSteps.step1.title'),
          description: t('storeRegistrationSteps.step1.description'),
          icon: <StoreIcon />,
        },
        {
          label: t('storeRegistrationSteps.step2.title'),
          description: t('storeRegistration.contactInfo.selfServiceDescription'),
          icon: <ContactIcon />,
        },
        {
          label: t('storeRegistrationSteps.step3.title'),
          description: t('storeRegistrationSteps.step3.description'),
          icon: <LocationIcon />,
        },
        {
          label: t('storeRegistrationSteps.step5.title'),
          description: t('storeRegistrationSteps.step5.description'),
          icon: <ReviewIcon />,
        }
      ]
    : [
        {
          label: t('storeRegistrationSteps.step1.title'),
          description: t('storeRegistrationSteps.step1.description'),
          icon: <StoreIcon />,
        },
        {
          label: t('storeRegistrationSteps.step2.title'),
          description: t('storeRegistrationSteps.step2.description'),
          icon: <ContactIcon />,
        },
        {
          label: t('storeRegistrationSteps.step3.title'),
          description: t('storeRegistrationSteps.step3.description'),
          icon: <LocationIcon />,
        },
        {
          label: t('storeRegistrationSteps.step4.title'),
          description: t('storeRegistrationSteps.step4.description'),
          icon: <BusinessHoursIcon />,
        },
        {
          label: t('storeRegistrationSteps.step5.title'),
          description: t('storeRegistrationSteps.step5.description'),
          icon: <ReviewIcon />,
        }
      ];

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReturnToLogin = () => {
    navigate('/management/team-member/login');
  };

  const performSubmit = async () => {
    setLoading(true);
    try {
      // Convert supported languages to enum values
      const supportedLanguages = formData.supportedLanguages.map(lang => lang.toUpperCase());
      const storeLanguage = (formData.storeLanguage || formData.supportedLanguages[0] || DEFAULT_LANGUAGE).toUpperCase();

      // Convert about to enum keys - only for supported languages
      const aboutWithEnumKeys = {};
      formData.supportedLanguages.forEach(lang => {
        aboutWithEnumKeys[lang.toUpperCase()] = formData.about[lang] || '';
      });

      const contacts = [];

      if (formData.email && formData.email.trim()) {
        contacts.push({
          type: 'Email',
          value: formData.email.trim(),
          label: 'Primary Email'
        });
      }

      const storePhone = composePhoneValue(formData.phoneCountryCode, formData.phoneNumber);
      if (storePhone) {
        contacts.push({
          type: 'Phone1',
          value: storePhone,
          label: 'Primary Phone'
        });
      }

      if (formData.website && formData.website.trim()) {
        contacts.push({
          type: 'Website',
          value: formData.website.trim(),
          label: 'Website'
        });
      }

      const hasAddressValue = [
        formData.address,
        formData.door,
        formData.additionalInfo,
        formData.city,
        formData.state,
        formData.zipCode,
        formData.country
      ].some(value => value && value.trim());

      if (isSelfService) {
        const registrationData = {
          owner: {
            name: formData.ownerName.trim(),
            email: formData.ownerEmail.trim(),
            phone: formData.ownerPhoneNumber.trim()
              ? {
                  countryCode: formData.ownerPhoneCountryCode,
                  number: formData.ownerPhoneNumber.trim()
                }
              : null
          },
          name: formData.name,
          slug: formData.slug,
          currency: formData.currency || null,
          about: Object.values(aboutWithEnumKeys).some(value => value && value.trim()) ? aboutWithEnumKeys : null,
          address: hasAddressValue ? {
            street: formData.address,
            door: formData.door,
            additionalInfo: formData.additionalInfo,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
            country: formData.country
          } : null,
          contacts: contacts.length > 0 ? contacts : null,
          timeZone: formData.timeZone || getDefaultTimeZone(),
          supportedLanguages: supportedLanguages,
          storeLanguage: storeLanguage,
          slotInterval: formData.slotInterval || 'Thirty',
          googleReviewUrl: formData.googleReviewUrl?.trim() || null,
          vatNumber: formData.vatNumber?.trim() || null
        };

        await StoreService.registerStoreOwner(registrationData);
        clearLocalStorage();
        isCompletingRegistration.current = true;
        navigate('/management/team-member/login', {
          replace: true,
          state: {
            registrationSuccess: true
          }
        });
        return;
      }

      // Convert business hours to enum values
      const dayEnumMap = {
        'sunday': 'Sunday',
        'monday': 'Monday',
        'tuesday': 'Tuesday',
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday'
      };

      const storeData = {
        name: formData.name,
        slug: formData.slug,
        currency: formData.currency,
        about: aboutWithEnumKeys,
        address: {
          street: formData.address,
          door: formData.door,
          additionalInfo: formData.additionalInfo,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country
        },
        contacts: contacts,
        timeZone: formData.timeZone || getDefaultTimeZone(),
        openingHours: Object.entries(formData.businessHours)
          .filter(([day, hours]) => !hours.closed && hours.periods?.length > 0)
          .map(([day, hours]) => ({
            weekDay: dayEnumMap[day.toLowerCase()],
            periods: hours.periods.map(p => ({ from: p.from, to: p.to }))
          })),
        supportedLanguages: supportedLanguages,
        storeLanguage: storeLanguage,
        slotInterval: formData.slotInterval || 'Thirty',
        disabled: false,
        googleReviewUrl: formData.googleReviewUrl?.trim() || null,
        vatNumber: formData.vatNumber?.trim() || null
      };

      const createResult = await StoreService.createStore(storeData);

      clearLocalStorage();
      isCompletingRegistration.current = true;
      await refreshStores();

      if (createResult?.requiresImmediatePaidBilling && createResult?.storeId) {
        navigate(`/management/stores/${createResult.storeId}/payment-settings`, {
          state: {
            billingSetupRequired: true,
            messageKey: 'storeRegistration.paidStoreCreatedMessage'
          }
        });
        return;
      }

      setSnackbar({
        open: true,
        message: createResult?.requiresImmediatePaidBilling === false && creationEligibility?.requiresPaidSubscription
          ? t('storeRegistration.paidStoreProvisionedMessage')
          : t('storeRegistration.success'),
        severity: 'success'
      });

      navigate('/management/stores');
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      const registrationErrorKeys = {
        StoreCreationRequiresBillingSetup: 'storeRegistration.errors.billingSetupRequired',
        PhoneNumberAlreadyInUse: 'storeRegistration.errors.phoneAlreadyInUse',
        EmailAlreadyInUse: 'storeRegistration.errors.emailAlreadyInUse',
        StoreSlugAlreadyTaken: 'storeRegistration.validation.slugTaken',
        StoreSlugInvalid: 'storeRegistration.validation.slugInvalid',
        StoreSlugReserved: 'storeRegistration.validation.slugReserved',
      };

      if (isSelfService && errorCode === 'PhoneNumberAlreadyInUse') {
        setActiveStep(1);
        setErrors((prev) => ({ ...prev, ownerPhoneNumber: t(registrationErrorKeys.PhoneNumberAlreadyInUse) }));
      } else if (isSelfService && errorCode === 'EmailAlreadyInUse') {
        setActiveStep(1);
        setErrors((prev) => ({ ...prev, ownerEmail: t(registrationErrorKeys.EmailAlreadyInUse) }));
      }

      setSnackbar({
        open: true,
        message: registrationErrorKeys[errorCode]
          ? t(registrationErrorKeys[errorCode])
          : error?.response?.data?.message || error.message || t('storeRegistration.error'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!acceptedLegalTerms) {
      setErrors({ legalTerms: 'legal.registration.termsRequired' });
      return;
    }

    if (!validateStep(activeStep)) {
      return;
    }

    if (isCreationBlocked) {
      return;
    }

    if (!isSelfService && !isAppAdmin && creationEligibility?.requiresPaidSubscription) {
      setConfirmPaidDialogOpen(true);
      return;
    }

    await performSubmit();
  };

  const handleConfirmPaidSubmit = async () => {
    setConfirmPaidDialogOpen(false);
    await performSubmit();
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Basic Info
        if (!formData.name.trim()) {
          newErrors.name = t('storeRegistration.validation.nameRequired');
        }
        const slugErrorKey = getSlugFormatErrorKey(formData.slug);
        if (slugErrorKey) {
          newErrors.slug = t(slugErrorKey);
        }
        if (!isSelfService && !formData.timeZone) {
          newErrors.timeZone = t('storeRegistration.validation.timeZoneRequired');
        }
        if (!isSelfService && formData.supportedLanguages.length === 0) {
          newErrors.languages = t('storeRegistration.validation.languagesRequired');
        }
        if (!isSelfService && !formData.storeLanguage) {
          newErrors.storeLanguage = t('storeRegistration.validation.storeLanguageRequired');
        } else if (!isSelfService && !formData.supportedLanguages.includes(formData.storeLanguage)) {
          newErrors.storeLanguage = t('storeRegistration.validation.storeLanguageMustBeSupported');
        }
        const missingAbout = formData.supportedLanguages.filter(
          lang => !formData.about[lang] || !formData.about[lang].trim()
        );
        if (!isSelfService && missingAbout.length > 0) {
          newErrors.about = 'storeRegistration.validation.aboutRequired';
        }
        break;

      case 1: // Contact Info
        if (isSelfService) {
          if (!formData.ownerName.trim()) {
            newErrors.ownerName = t('storeRegistration.validation.ownerNameRequired');
          }
          if (!formData.ownerEmail.trim()) {
            newErrors.ownerEmail = t('storeRegistration.validation.ownerEmailRequired');
          } else if (!/\S+@\S+\.\S+/.test(formData.ownerEmail)) {
            newErrors.ownerEmail = t('storeRegistration.validation.emailInvalid');
          }
          if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = t('storeRegistration.validation.emailInvalid');
          }
        } else {
          if (!formData.email.trim()) {
            newErrors.email = t('storeRegistration.validation.emailRequired');
          } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = t('storeRegistration.validation.emailInvalid');
          }
          if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = t('storeRegistration.validation.phoneRequired');
          }
        }
        break;

      case 2: // Location Info
        if (!isSelfService || [formData.address, formData.city, formData.state, formData.zipCode, formData.country, formData.door, formData.additionalInfo].some(value => value && value.trim())) {
          if (!formData.address.trim()) {
            newErrors.address = t('storeRegistration.validation.addressRequired');
          }
          if (!formData.city.trim()) {
            newErrors.city = t('storeRegistration.validation.cityRequired');
          }
          if (!formData.state.trim()) {
            newErrors.state = t('storeRegistration.validation.stateRequired');
          }
          if (!formData.zipCode.trim()) {
            newErrors.zipCode = t('storeRegistration.validation.zipCodeRequired');
          }
          if (!formData.country.trim()) {
            newErrors.country = t('storeRegistration.validation.countryRequired');
          }
        }
        break;

      case 3: // Business Hours
        // Validate business hours - at least one day must be enabled
        const enabledDays = Object.values(formData.businessHours).filter(hours => !hours.closed && hours.periods?.length > 0);
        if (enabledDays.length === 0) {
          newErrors.businessHours = t('storeRegistration.validation.businessHoursRequired');
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateFormData = (updates) => {
    setFormData((prev) => {
      const nextFormData = { ...prev, ...updates };
      const nextSupportedLanguages = nextFormData.supportedLanguages || [];

      if (nextSupportedLanguages.length > 0 && !nextSupportedLanguages.includes(nextFormData.storeLanguage)) {
        nextFormData.storeLanguage = nextSupportedLanguages[0];
      }

      if (nextSupportedLanguages.length === 0) {
        nextFormData.storeLanguage = '';
      }

      return nextFormData;
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return <StoreBasicInfo formData={formData} updateFormData={updateFormData} errors={errors} setErrors={setErrors} mode={mode} syncSlugFromName />;
      case 1:
        return <StoreContactInfo formData={formData} updateFormData={updateFormData} errors={errors} setErrors={setErrors} mode={mode} />;
      case 2:
        return <StoreLocationInfo formData={formData} updateFormData={updateFormData} errors={errors} setErrors={setErrors} mode={mode} />;
      case 3:
        return isSelfService
          ? <StoreReview formData={formData} mode={mode} />
          : <StoreBusinessHoursInfo formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 4:
        return <StoreReview formData={formData} mode={mode} />;
      default:
        return null;
    }
  };

  if (!isSelfService && (authLoading || (!isAppAdmin && permissionsLoading) || creationEligibilityLoading)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!isSelfService && user && !canCreateStore) {
    return (
      <ManagementPageLayout
        id="store-registration-page"
        containerId="store-registration-header"
        titleId="store-registration-title"
        subtitleId="store-registration-subtitle"
        pageTitle={t('storeRegistration.title')}
        pageSubtitle={t('storeRegistration.subtitle')}
        contentId="store-registration-main-content"
        contentSx={{
          maxWidth: { lg: '960px' },
          width: { xs: '100%', lg: '960px' }
        }}
      >
        <ManagementPageCard id="store-registration-card">
          <Alert severity="error">{t('store.accessDenied')}</Alert>
        </ManagementPageCard>
      </ManagementPageLayout>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {isSelfService ? (
        <PageSeo
          title={t('storeRegistration.seoTitle')}
          description={t('storeRegistration.seoDescription')}
          path="/register-store"
        />
      ) : null}
      {isSelfService ? (
        <Box sx={{ display: { xs: 'none', sm: 'block' }, position: 'absolute', top: 24, right: 24, zIndex: 2 }}>
          <AuthHeaderActions />
        </Box>
      ) : null}
      <ManagementPageLayout
      id="store-registration-page"
      containerId="store-registration-header"
      titleId="store-registration-title"
      subtitleId="store-registration-subtitle"
      pageTitle={isSelfService ? t('storeRegistration.selfServiceTitle') : t('storeRegistration.title')}
      pageSubtitle={isSelfService ? t('storeRegistration.selfServiceSubtitle') : t('storeRegistration.subtitle')}
      contentId="store-registration-main-content"
      contentSx={{
        maxWidth: { lg: '960px' },
        width: { xs: '100%', lg: '960px' }
      }}
      leadingContent={isSelfService ? (
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            width: '100%',
            justifyContent: 'flex-end',
            alignItems: 'center',
            px: 1,
            mb: 1,
            minHeight: 56,
            boxSizing: 'border-box',
          }}
        >
          <AuthHeaderActions />
        </Box>
      ) : undefined}
    >
        <ManagementPageCard id="store-registration-card">
          {extraStoreBlocked ? (
            <Box sx={{ p: { xs: 2, sm: 4 } }}>
              <StoreCreationBlockedAlert
                billingSetupPath={billingSetupPath}
                titleKey="storePlan.extraStoreTitle"
                messageKey="storePlan.extraStoreMessage"
                actionKey="storePlan.upgradeCta"
              />
            </Box>
          ) : (
          <>
          {/* Custom Stepper */}
          <Box id="store-registration-stepper-container" sx={{
            background: BRAND.background,
            borderBottom: `1px solid ${BRAND.line}`,
            p: 4
          }}>
            {isSelfService && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
                <Button
                  id="store-registration-return-to-login-button"
                  variant="outlined"
                  onClick={handleReturnToLogin}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    borderRadius: 999,
                    px: 2,
                    py: 0.75,
                    textTransform: 'none',
                    fontWeight: 700,
                    color: BRAND.accentText,
                    borderColor: BRAND.line,
                    backgroundColor: BRAND.surface,
                    '&:hover': {
                      borderColor: BRAND.accent,
                      backgroundColor: brandAlpha('accent', 0.08),
                    },
                  }}
                >
                  {t('login.backToLogin')}
                </Button>
              </Box>
            )}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'stretch', // This will make all items stretch to the same height
              position: 'relative'
            }}>
              {steps.map((step, index) => (
                <Box key={index} sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  position: 'relative',
                  overflow: 'visible',
                  justifyContent: 'flex-start' // Align content to top
                }}>
                  {/* Step Circle */}
                  <Box
                    id={`store-registration-step-icon-${index + 1}`}
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: activeStep >= index ? BRAND.accent : BRAND.surface,
                      color: activeStep >= index ? BRAND.onAccent : BRAND.textMuted,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem',
                      fontWeight: 'bold',
                      border: `3px solid ${activeStep >= index ? BRAND.accent : BRAND.line}`,
                      boxShadow: 'none',
                      transition: 'all 0.3s ease',
                      zIndex: 2,
                      position: 'relative'
                    }}
                  >
                    {activeStep > index ? <CheckIcon /> : step.icon}
                  </Box>

                  {/* Step Label */}
                  <Typography
                    id={`store-registration-step-label-${index + 1}`}
                    sx={{
                      color: activeStep >= index ? BRAND.text : BRAND.textMuted,
                      fontWeight: activeStep >= index ? 700 : 500,
                      fontSize: '0.9rem',
                      mt: 1.5,
                      textAlign: 'center',
                      display: { xs: 'none', sm: 'block' },
                      lineHeight: '1.25rem',
                      width: '100%',
                      flex: 1 // This will make the label take up remaining space
                    }}
                  >
                    {step.label}
                  </Typography>

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <Box sx={{
                      position: 'absolute',
                      top: '25px', // Keep at circle center (25px from top of circle)
                      left: 'calc(50% + 25px)',
                      right: 'calc(-50% + 25px)',
                      height: '3px',
                      background: activeStep > index ? BRAND.accent : BRAND.line,
                      zIndex: 1
                    }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Step Content */}
          <Box id="store-registration-step-content" sx={{ p: { xs: 1, sm: 4, md: 6 } }}>
            {!isSelfService && !isAppAdmin && creationEligibility && (
              <>
                {isCreationBlocked && (
                  <StoreCreationBlockedAlert
                    billingSetupPath={billingSetupPath}
                    titleKey={extraStoreBlocked ? 'storePlan.extraStoreTitle' : 'storeRegistration.billingRequiredTitle'}
                    messageKey={extraStoreBlocked ? 'storePlan.extraStoreMessage' : 'storeRegistration.billingRequiredMessage'}
                    actionKey={extraStoreBlocked ? 'storePlan.upgradeCta' : 'storeRegistration.billingRequiredAction'}
                    sx={{ mb: 3 }}
                  />
                )}
                {!isCreationBlocked && creationEligibility.requiresPaidSubscription && (
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    {paidStoreWarningMessage}
                  </Alert>
                )}
              </>
            )}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                id={`store-registration-step-${activeStep + 1}-content`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent(activeStep)}
              </motion.div>
            </AnimatePresence>

            {activeStep === steps.length - 1 && (
              <LegalTermsCheckbox
                checked={acceptedLegalTerms}
                onChange={(value) => {
                  setAcceptedLegalTerms(value);
                  if (value && errors.legalTerms) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.legalTerms;
                      return next;
                    });
                  }
                }}
                error={errors.legalTerms}
              />
            )}

            {/* Navigation */}
            <Box
              id="store-registration-navigation"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 4,
                pt: 3,
                borderTop: '1px solid rgba(0,0,0,0.1)'
              }}
            >
              <Button
                id="store-registration-back-button"
                variant="outlined"
                onClick={handleBack}
                disabled={activeStep === 0}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  borderColor: BRAND.accent,
                  color: BRAND.accentText,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: BRAND.accentDark,
                    backgroundColor: brandAlpha('accent', 0.1),
                  },
                  '&:disabled': {
                    borderColor: brandAlpha('accent', 0.3),
                    color: brandAlpha('accent', 0.3),
                  }
                }}
              >
                {t('common.back')}
              </Button>

              <Button
                id={activeStep === steps.length - 1 ? "store-registration-submit-button" : "store-registration-next-button"}
                variant="contained"
                onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
                disabled={loading || isCreationBlocked || (activeStep === steps.length - 1 && !acceptedLegalTerms)}
                endIcon={activeStep === steps.length - 1 ?
                  (loading ? <CircularProgress size={20} /> : <CheckIcon />) :
                  <ArrowForwardIcon />
                }
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  background: BRAND.accent,
                  color: BRAND.onAccent,
                  boxShadow: 'none',
                  '&:hover': {
                    background: BRAND.accentDark,
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    background: '#ccc',
                    transform: 'none',
                    boxShadow: 'none'
                  }
                }}
              >
                {loading ? t('common.submitting') :
                  activeStep === steps.length - 1 ? t('common.submit') : t('common.next')
                }
              </Button>
            </Box>
          </Box>
          </>
          )}
        </ManagementPageCard>
      {/* Success/Error Snackbar */}
      <Snackbar
        id="store-registration-snackbar"
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          id={`store-registration-alert-${snackbar.severity}`}
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: 2,
            fontWeight: 600
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={confirmPaidDialogOpen}
        onClose={() => setConfirmPaidDialogOpen(false)}
        onConfirm={handleConfirmPaidSubmit}
        title={t('storeRegistration.paidStoreConfirmTitle')}
        message={paidStoreConfirmMessage}
        confirmText={t('storeRegistration.paidStoreConfirmAction')}
        confirmColor="primary"
      />
    </ManagementPageLayout>
    </Box>
  );
};

export default StoreRegistrationPage;
